import { useEffect, useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { highlight } from '@/lib/shiki';

interface Props {
    code: string;
    lang: string;
    showCopy?: boolean;
}

export default function CodeBlock({ code, lang, showCopy = true }: Props) {
    const [html, setHtml] = useState<string>('');
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        let cancelled = false;
        highlight(code, lang).then((h) => { if (!cancelled) setHtml(h); });
        return () => { cancelled = true; };
    }, [code, lang]);

    const onCopy = async () => {
        try { await navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 1500); }
        catch { /* */ }
    };

    return (
        <div className="relative group" style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)' }}>
            {showCopy && (
                <button
                    onClick={onCopy}
                    className="absolute top-2 right-2 p-1.5 rounded-md opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ backgroundColor: 'var(--surface-hover)', border: '1px solid var(--border-strong)', color: 'var(--muted)' }}
                    aria-label="Copy"
                >
                    {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                </button>
            )}
            {html ? (
                <div
                    className="shiki-wrapper text-xs leading-relaxed [&_pre]:!m-0 [&_pre]:!p-3 [&_pre]:overflow-auto"
                    dangerouslySetInnerHTML={{ __html: html }}
                />
            ) : (
                <pre className="text-xs leading-relaxed m-0 p-3 overflow-auto" style={{ backgroundColor: 'var(--code-bg)', color: 'var(--ink)' }}>
                    <code>{code}</code>
                </pre>
            )}
        </div>
    );
}
